import Button from "components/ui/Button";
import {
	Calendar,
	Check,
	Clock,
	Eye,
	Mail,
	MessageCircle,
	Phone,
	User,
	X,
} from "lucide-react";
import { formatDateTime } from "../../../utils/formatDate";

interface ViewingRequest {
	[key: string]: any;
}

interface ViewingRequestsProps {
	requests: ViewingRequest[];
	onRequestAction: (request: ViewingRequest, action: string) => void;
	filters: {
		search?: string;
		[key: string]: any;
	};
}

const ViewingRequests = ({
	requests = [],
	onRequestAction,
	filters,
}: ViewingRequestsProps) => {
	// Filter requests based on current filters (search is already applied in parent)
	// Status filter is also applied in parent, so we just need to handle search here
	const filteredRequests = requests?.filter((request) => {
		if (filters?.search) {
			const searchTerm = filters?.search?.toLowerCase();
			const propertyName = (request?.property_name || "").toLowerCase();
			const buyerName = (request?.buyer_name || "").toLowerCase();
			const buyerEmail = (request?.buyer_email || "").toLowerCase();
			return (
				propertyName.includes(searchTerm) ||
				buyerName.includes(searchTerm) ||
				buyerEmail.includes(searchTerm)
			);
		}
		return true;
	});

	// Get status badge color and label
	const getStatusBadge = (status: string) => {
		const statusLower =
			typeof status === "string"
				? status.toLowerCase()
				: String(status || "").toLowerCase();
		switch (statusLower) {
			case "pending":
				return {
					label: "Pending",
					className: "bg-warning/10 text-warning border-warning/20",
				};
			case "scheduled":
				return {
					label: "Scheduled",
					className: "bg-blue-500/10 text-blue-500 border-blue-500/20",
				};
			case "confirmed":
				return {
					label: "Confirmed",
					className: "bg-green-500/10 text-green-500 border-green-500/20",
				};
			case "completed":
				return {
					label: "Completed",
					className: "bg-purple-500/10 text-purple-500 border-purple-500/20",
				};
			case "cancelled":
				return {
					label: "Cancelled",
					className: "bg-destructive/10 text-destructive border-destructive/20",
				};
			default:
				return {
					label: statusLower.charAt(0).toUpperCase() + statusLower.slice(1),
					className: "bg-muted text-muted-foreground border-border",
				};
		}
	};

	// Using centralized formatDateTime utility
	const formatTime = (timeOrDateTime: string | undefined) => {
		if (!timeOrDateTime) return "N/A";
		const formatted = formatDateTime(timeOrDateTime);
		// Extract time part (everything after the date)
		const timeMatch = formatted.match(/\d{1,2}:\d{2}\s*(AM|PM)/i);
		return timeMatch ? timeMatch[0] : formatted.split(" ").slice(-2).join(" ");
	};

	const getPriorityColor = (priority: string) => {
		const colors: Record<string, string> = {
			high: "bg-red-100 text-red-800 border-red-200 dark:bg-red-950/20 dark:text-red-400",
			medium:
				"bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-950/20 dark:text-yellow-400",
			low: "bg-green-100 text-green-800 border-green-200 dark:bg-green-950/20 dark:text-green-400",
		};
		return colors[priority] || colors.medium;
	};

	if (filteredRequests?.length === 0) {
		return (
			<div className="p-6">
				<div className="flex items-center justify-between mb-6">
					<h2 className="text-lg font-semibold text-foreground">
						Viewing Requests
					</h2>
				</div>
				<div className="text-center py-12">
					<div className="w-16 h-16 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
						<Calendar className="w-6 h-6 text-muted-foreground" />
					</div>
					<h3 className="text-lg font-semibold text-foreground mb-2">
						No Viewings Found
					</h3>
					<p className="text-muted-foreground">
						{filters?.search
							? "No viewings match your search criteria."
							: filters?.status && filters.status !== "all"
							? `No viewings with status "${filters.status}" found.`
							: "No viewings available."}
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="p-6">
			<div className="flex items-center justify-between mb-6">
				<h2 className="text-lg font-semibold text-foreground">
					Viewing Requests
				</h2>
				<div className="text-sm text-muted-foreground">
					{filteredRequests?.length} viewing
					{filteredRequests?.length !== 1 ? "s" : ""}
				</div>
			</div>
			<div className="space-y-4">
				{filteredRequests?.map((request: any) => (
					<div
						key={request?.id}
						className="bg-card border border-border rounded-lg p-4 sm:p-6 overflow-hidden"
					>
						<div className="flex flex-col sm:flex-row items-start sm:items-start justify-between gap-3 mb-4">
							<div className="flex-1 min-w-0">
								<div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
									<h3 className="text-lg font-semibold text-foreground truncate flex-1 min-w-0">
										{request?.property_name || "Unknown Property"}
									</h3>
									{(() => {
										const statusBadge = getStatusBadge(request?.status || "");
										return (
											<span
												className={`px-2 py-1 rounded-full text-xs font-medium border flex-shrink-0 ${statusBadge.className}`}
											>
												{statusBadge.label}
											</span>
										);
									})()}
								</div>
								<p className="text-sm text-muted-foreground truncate">
									{request?.property_address || ""}
								</p>
							</div>

							<div className="text-left sm:text-right flex-shrink-0">
								<p className="text-sm text-muted-foreground">Requested</p>
								<p className="text-sm font-medium text-foreground whitespace-nowrap">
									{formatDateTime(request?.created_at)}
								</p>
							</div>
						</div>

						{/* Buyer Information */}
						<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 bg-muted rounded-lg">
							<div className="flex items-center gap-3 min-w-0">
								<div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center flex-shrink-0">
									<User className="w-5 h-5" />
								</div>
								<div className="min-w-0 flex-1">
									<p className="text-sm font-medium text-foreground truncate">
										{request?.buyer_name || "N/A"}
									</p>
									<p className="text-xs text-muted-foreground">
										Potential Buyer
									</p>
								</div>
							</div>

							<div className="flex items-center gap-3 min-w-0">
								<Phone className="w-4 h-4 text-muted-foreground flex-shrink-0" />
								<div className="min-w-0 flex-1">
									<p className="text-sm text-foreground truncate">
										{request?.buyer_phone || "N/A"}
									</p>
									<p className="text-xs text-muted-foreground">Phone</p>
								</div>
							</div>

							<div className="flex items-center gap-3 min-w-0">
								<Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
								<div className="min-w-0 flex-1">
									<p className="text-sm text-foreground truncate">
										{request?.buyer_email || "N/A"}
									</p>
									<p className="text-xs text-muted-foreground">Email</p>
								</div>
							</div>
						</div>

						{/* Preferred Viewing Details */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
							<div className="flex items-center gap-3 p-3 bg-background rounded-lg border border-border">
								<Calendar className="w-5 h-5 text-muted-foreground" />
								<div>
									<p className="text-sm font-medium text-foreground">
										Viewing Date
									</p>
									<p className="text-sm text-muted-foreground">
										{formatDateTime(request?.viewing_date)}
									</p>
								</div>
							</div>

							<div className="flex items-center gap-3 p-3 bg-background rounded-lg border border-border">
								<Clock className="w-5 h-5 text-muted-foreground" />
								<div>
									<p className="text-sm font-medium text-foreground">
										Viewing Time
									</p>
									<p className="text-sm text-muted-foreground">
										{formatTime(request?.viewing_date)}
									</p>
								</div>
							</div>
						</div>

						{/* Notes/Message */}
						{request?.notes && (
							<div className="mb-4 p-3 bg-background rounded-lg border border-border overflow-hidden">
								<p className="text-sm font-medium text-foreground mb-1">
									Notes:
								</p>
								<p className="text-sm text-muted-foreground break-words">
									{request?.notes}
								</p>
							</div>
						)}

						{/* Buyer Qualification Info */}
						{request?.qualification && (
							<div className="mb-4 p-3 bg-background rounded-lg border border-border">
								<p className="text-sm font-medium text-foreground mb-2">
									Buyer Qualification
								</p>
								<div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
									<div>
										<span className="text-muted-foreground">Budget Range:</span>
										<span className="ml-2 text-foreground font-medium">
											{request?.qualification?.budgetRange}
										</span>
									</div>
									<div>
										<span className="text-muted-foreground">Pre-approved:</span>
										<span className="ml-2 text-foreground font-medium">
											{request?.qualification?.preApproved ? "Yes" : "No"}
										</span>
									</div>
									<div>
										<span className="text-muted-foreground">Timeline:</span>
										<span className="ml-2 text-foreground font-medium">
											{request?.qualification?.timeline}
										</span>
									</div>
								</div>
							</div>
						)}

						{/* Actions */}
						<div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-4 border-t border-border">
							<div className="flex flex-wrap items-center gap-2 flex-1">
								<Button
									variant="outline"
									size="sm"
									onClick={() => onRequestAction(request, "profile")}
									className="flex items-center gap-2 flex-1 sm:flex-initial min-w-0"
								>
									<Eye className="w-4 h-4 flex-shrink-0" />
									<span className="truncate">Profile</span>
								</Button>
								<Button
									variant="outline"
									size="sm"
									onClick={() => onRequestAction(request, "chat")}
									className="flex items-center gap-2 flex-1 sm:flex-initial min-w-0"
								>
									<MessageCircle className="w-4 h-4 flex-shrink-0" />
									<span className="truncate">Chat</span>
								</Button>
							</div>

							<div className="flex flex-wrap items-center gap-2 flex-1 sm:flex-initial sm:justify-end">
								<Button
									variant="outline"
									size="sm"
									onClick={() => onRequestAction(request, "reject")}
									className="flex items-center gap-2 text-destructive hover:text-destructive flex-1 sm:flex-initial min-w-0"
								>
									<X className="w-4 h-4 flex-shrink-0" />
									<span className="truncate">Decline</span>
								</Button>
								<Button
									size="sm"
									onClick={() => onRequestAction(request, "approve")}
									className="flex items-center gap-2 flex-1 sm:flex-initial min-w-0"
								>
									<Check className="w-4 h-4 flex-shrink-0" />
									<span className="truncate">Approve</span>
								</Button>
							</div>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

export default ViewingRequests;
